import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import { apiFetch } from '@/pages/marketplace/api';

/**
 * The one-line version of a store's return policy, for product pages — a shopper shouldn't
 * have to buy something to find out whether it can come back (owner, Jun 2026).
 *
 * Nothing here speaks for the platform. Returns are the store's to run, so the store's terms
 * are the only promise a shopper is shown.
 */
export function ReturnPolicySummary({ vendorId, productReturnable = true, productConsumable = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    apiFetch(`/marketplace/return-policy/${vendorId}`).then(setData).catch(() => setData(null));
  }, [vendorId]);

  if (!data) return null;
  const p = data.policy || {};

  let line;
  if (productConsumable) line = "This item is a consumable and can't be returned once shipped.";
  else if (productReturnable === false) line = 'Final sale — this item cannot be returned.';
  else if (!p.accepts_returns) line = p.non_returnable_note || `${data.store_name} does not accept returns.`;
  else {
    const bits = [`${p.window_days}-day returns`];
    if (p.resolution === 'replace') bits.push('replacement only');
    else if (p.resolution === 'either') bits.push('refund or replacement');
    bits.push(`return shipping paid by ${p.return_shipping_paid_by === 'vendor' ? 'the store' : 'you'}`);
    if (p.restocking_fee_percent > 0) bits.push(`${p.restocking_fee_percent}% restocking fee`);
    line = bits.join(' · ');
  }

  return (
    <div
      data-testid="product-return-summary"
      style={{
        border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, padding: '12px 14px',
        margin: '16px 0 0', background: 'rgba(0,0,0,0.02)', fontSize: 13, lineHeight: 1.6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <RotateCcw size={14} style={{ marginTop: 3, flexShrink: 0, opacity: 0.6 }} />
        <div>
          <strong style={{ display: 'block', marginBottom: 2 }}>Returns</strong>
          <span data-testid="product-return-line">{line}</span>
          <button
            type="button"
            onClick={() => navigate(`/dashboard/marketplace/store/${data.store_slug}?tab=returns`)}
            data-testid="product-read-full-policy"
            style={{
              display: 'block', marginTop: 6, padding: 0, border: 'none', background: 'none',
              font: 'inherit', textDecoration: 'underline', cursor: 'pointer', opacity: 0.75,
            }}
          >
            Read {data.store_name}&apos;s full return policy
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReturnPolicySummary;
